import type {Component, Editor} from 'grapesjs';
import type {CanvasState, EmailVersionData, ParsedPage, ParsedTemplateContent, VersionHistorySession, VersionHistoryUndoStack} from './email-maker-version-history-panel.types';

/**
 * Modifier class on the email maker navbar that swaps the options with the version-history controls
 */
const NAVBAR_VERSION_HISTORY_MODE_CLASS = '-version-history-mode';

/**
 * Editing shortcuts (held with ⌘/Ctrl) that must be inert while viewing a version
 */
const READ_ONLY_BLOCKED_MODIFIER_KEYS = new Set(['z', 'c', 'v', 'x', 'd', 'l', 'b', 'i', 'u', 's']);
const READ_ONLY_BLOCKED_SHIFT_MODIFIER_KEYS = new Set(['r', 'j']);

/**
 * GrapesJS selection-navigation keys (no modifier) that should not move the canvas selection while viewing.
 */
const READ_ONLY_BLOCKED_NAV_KEYS = new Set(['tab', 'a', 's', 'd', 'w', 'escape']);

/**
 * Per-editor session state created on enter and removed on exit. Out-of-session calls no-op instead of
 * undoing down a stale baseline of 0, and a torn-down editor takes its session with it (WeakMap) rather
 * than leaving stale state behind for the next editor instance.
 */
const versionHistorySessionByEditor = new WeakMap<Editor, VersionHistorySession>();

const getUndoStack = (editor: Editor): VersionHistoryUndoStack => {
  return editor.UndoManager.getStack() as unknown as VersionHistoryUndoStack;
};

/**
 * Fetches the stored version-history metadata for the template, newest first.
 */
export const fetchEmailVersions = async (idEmailTemplate: string): Promise<EmailVersionData[]> => {
  const response = (await window.PMW.readLocal('emailmaker/getTemplateVersions', {idEmailTemplate})) as EmailVersionData[];
  return Array.isArray(response) ? response : [];
};

export const fetchEmailVersionContent = async (idEmailTemplate: string, versionId: number): Promise<string> => {
  const response = (await window.PMW.readLocal('emailmaker/getTemplateVersionContent', {idEmailTemplate, idVersion: versionId})) as {jsonContent?: string};
  if (typeof response?.jsonContent !== 'string') {
    throw new Error(`Could not fetch content for email version ${versionId}`);
  }
  return response.jsonContent;
};

/**
 * Parses a stored version's jsonContent into the components + styles to apply to the canvas.
 */
const getCanvasStateFromSnapshot = (jsonContent: string): CanvasState => {
  const parsed = JSON.parse(jsonContent) as ParsedTemplateContent;
  const pages = JSON.parse(parsed.pages) as ParsedPage[];
  const styles = JSON.parse(parsed.styles) as unknown;
  return {
    components: pages[0]?.frames[0]?.component.components ?? [],
    styles,
  };
};

/**
 * True when the keystroke targets a field the user can legitimately type into (e.g. a panel input), so the
 * read-only canvas lock must not swallow it.
 */
const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const tagName = target.tagName.toLowerCase();
  return target.isContentEditable || tagName === 'input' || tagName === 'textarea' || tagName === 'select';
};

/**
 * Capture-phase keydown guard (installed while viewing) that stops GrapesJS' bubble-phase keymaps — notably ⌘/Ctrl+Z,
 * which would desync the preview tracking. Editing combos are also preventDefault-ed; nav keys only stopped so Tab still works.
 */
const blockEditingKeydownWhileViewing = (event: KeyboardEvent): void => {
  if (isEditableTarget(event.target)) {
    return;
  }

  const key = event.key.toLowerCase();
  const hasModifier = event.metaKey || event.ctrlKey;

  if (key === 'delete' || key === 'backspace') {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  if (hasModifier && (READ_ONLY_BLOCKED_MODIFIER_KEYS.has(key) || (event.shiftKey && READ_ONLY_BLOCKED_SHIFT_MODIFIER_KEYS.has(key)))) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  if (!hasModifier && READ_ONLY_BLOCKED_NAV_KEYS.has(key)) {
    event.stopImmediatePropagation();
  }
};

const lockCanvasForViewing = (editor: Editor): void => {
  const canvasBody = editor.Canvas.getBody();
  if (canvasBody) {
    canvasBody.style.pointerEvents = 'none';
  }
  document.addEventListener('keydown', blockEditingKeydownWhileViewing, true);
};

const unlockCanvasAfterViewing = (editor: Editor): void => {
  const canvasBody = editor.Canvas.getBody();
  if (canvasBody) {
    canvasBody.style.pointerEvents = '';
  }
  document.removeEventListener('keydown', blockEditingKeydownWhileViewing, true);
};

/**
 * Starts a version-history session: anchors the undo baseline + redo tail, locks the canvas read-only, swaps the navbar to VH controls.
 */
export const enterVersionHistoryMode = (editor: Editor): void => {
  const baselinePointer = editor.UndoManager.getPointer();
  versionHistorySessionByEditor.set(editor, {
    baselinePointer,
    savedRedoTail: getUndoStack(editor).models.slice(baselinePointer + 1),
  });
  lockCanvasForViewing(editor);
  document.getElementById('nav')?.classList.add(NAVBAR_VERSION_HISTORY_MODE_CLASS);
};

/**
 * Reverts any active preview by undoing back down to the baseline pointer. Undo re-inserts the stored
 * original content wrapper, so the user's prior undo history stays valid.
 */
const revertActivePreview = (editor: Editor): void => {
  const session = versionHistorySessionByEditor.get(editor);
  if (!session) {
    return;
  }

  const undoManager = editor.UndoManager;
  while (undoManager.getPointer() > session.baselinePointer && undoManager.hasUndo()) {
    undoManager.undo();
  }
};

/**
 * Removes every stack entry above the current pointer
 */
const clearRedoTailFromPreview = (editor: Editor): void => {
  const stack = getUndoStack(editor);
  const redoTail = stack.models.slice(editor.UndoManager.getPointer() + 1);
  if (redoTail.length > 0) {
    stack.remove(redoTail);
  }
};

/**
 * Re-attaches the user's pre-version-history redo entries (captured on enter) that previewing discarded
 */
const restoreUserRedoTail = (editor: Editor): void => {
  const session = versionHistorySessionByEditor.get(editor);
  if (!session) {
    return;
  }

  if (session.savedRedoTail.length > 0) {
    getUndoStack(editor).add(session.savedRedoTail);
    session.savedRedoTail = [];
  }
};

/**
 * Applies a stored version as a transient
 */
export const previewVersion = (editor: Editor, jsonContent: string): void => {
  if (!versionHistorySessionByEditor.has(editor)) {
    return;
  }

  const state = getCanvasStateFromSnapshot(jsonContent);
  revertActivePreview(editor);

  const versionWrapper = state.components.find((component) => component.type === window.PMW.emails.components.container.PMW_WRAPPER);
  const currentWrapper = editor.runCommand(window.PMW.emails.commands.GET_MAIN_WRAPPER) as Component | undefined;
  if (versionWrapper && currentWrapper) {
    currentWrapper.replaceWith(versionWrapper);
  }
  editor.setStyle(state.styles);
};

/**
 * Returns the canvas to the working design (used by the "Current version" row and by Back).
 */
export const revertToCurrentVersion = (editor: Editor): void => {
  revertActivePreview(editor);
};

export const commitRestore = (editor: Editor): void => {
  const session = versionHistorySessionByEditor.get(editor);
  if (!session) {
    return;
  }

  session.savedRedoTail = [];
  session.baselinePointer = editor.UndoManager.getPointer();
};

/**
 * Persists the just-restored content: it was applied while autosave was off, so an explicit cache store is needed
 * or the tab-hide beacon flushes the stale cache over it. Call after exitVersionHistoryMode so autosave is back on.
 */
export const persistRestoredTemplate = (editor: Editor): void => {
  void editor.runCommand(window.PMW.emails.commands.SAVE_TO_CACHE);
};

/**
 * Tears down version-history mode: reconciles the redo stack, then restores the canvas, navbar, panel, selection, blocks, and autosave.
 */
export const exitVersionHistoryMode = (editor: Editor): void => {
  clearRedoTailFromPreview(editor);
  restoreUserRedoTail(editor);
  versionHistorySessionByEditor.delete(editor);
  window.PMW.emails.IS_IN_VERSION_HISTORY_MODE = false;
  unlockCanvasAfterViewing(editor);
  document.getElementById('nav')?.classList.remove(NAVBAR_VERSION_HISTORY_MODE_CLASS);
  editor.runCommand(window.PMW.emails.commands.CLOSE_SIDE_PANEL, {title: window.i18next.t('pmwjs_version_history')});

  const wrapper = editor.runCommand(window.PMW.emails.commands.GET_MAIN_WRAPPER) as Component;
  editor.select(wrapper);

  const blocksManagerContainer = document.getElementById(window.PMW.emails.blocks.ID_BLOCKS_MANAGER_CONTAINER);
  blocksManagerContainer?.classList.remove('_invisible');

  editor.runCommand(window.PMW.emails.commands.SET_AUTOSAVE, {autosave: true});
};
